"use client";

import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface CircularProgressProps {
  percentage: number;
  color?: string;
  size?: number;
  thickness?: number;
  className?: string;
  showPercentage?: boolean;
  animate?: boolean;
  duration?: number;
  animationDelay?: number;
  animationEasing?: string;
  animationDirection?: "clockwise" | "counterclockwise";
}

export function ProgressCircle({
  percentage = 0,
  color = "teal",
  size = 74,
  thickness = 5,
  className = "",
  showPercentage = true,
  animate = true,
  duration = 1500,
  animationDelay = 0,
  animationEasing = "cubic-bezier(0.34, 1.56, 0.64, 1)",
  animationDirection = "clockwise",
}: CircularProgressProps) {
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const circleRef = useRef<SVGCircleElement>(null);
  const animationRef = useRef<number | null>(null);

  // Clamp percentage between 0 and 100
  const clampedPercentage = Math.min(100, Math.max(0, percentage));

  // Calculate the circumference of the circle
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  // Color mapping
  const colorMap: Record<string, string> = {
    teal: "#0d9488",
    "teal-500": "#14b8a6",
    black: "#000",
    red: "#F34627",
  };

  const strokeColor = colorMap[color] || color;

  useEffect(() => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (!animate) {
      setDisplayPercentage(clampedPercentage);
      if (circleRef.current) {
        const offset =
          circumference - (clampedPercentage / 100) * circumference;
        circleRef.current.style.strokeDashoffset = `${offset}px`;
      }
      return;
    }

    // Delay the animation start if needed
    setTimeout(() => {
      const startTime = performance.now();
      const startPercentage = 0;
      const endPercentage = clampedPercentage;

      const animateProgress = (timestamp: number) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Apply easing function (default is cubic-bezier for a nice bounce effect)
        const easedProgress = progress;

        // Calculate current percentage
        const currentPercentage = Math.round(
          startPercentage + (endPercentage - startPercentage) * easedProgress,
        );
        setDisplayPercentage(currentPercentage);

        // Calculate and set the stroke-dashoffset
        if (circleRef.current) {
          const offset =
            circumference - (currentPercentage / 100) * circumference;
          circleRef.current.style.strokeDashoffset = `${offset}px`;
        }

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animateProgress);
        }
      };

      animationRef.current = requestAnimationFrame(animateProgress);
    }, animationDelay);

    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [clampedPercentage, animate, duration, circumference, animationDelay]);

  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center text-base",
        className,
      )}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={clampedPercentage}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          transform:
            animationDirection === "clockwise"
              ? "rotate(-90deg)"
              : "rotate(90deg)",
        }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth={thickness}
        />

        {/* Progress circle */}
        <circle
          ref={circleRef}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={thickness}
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          style={{
            transition: animate ? `none` : "stroke-dashoffset 0.5s ease",
          }}
        />
      </svg>

      {showPercentage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-card-foreground font-semibold`}>
            {displayPercentage}%
          </span>
        </div>
      )}
    </div>
  );
}
